export type ExternalPerson = {
  id: number;
  email: string;
  name: string | null;
  // Whether a queued invite request is waiting on Ordinal staff. Queueing
  // never sends email from the app; staff send or reject in Madmin.
  invite_pending: boolean;
};

export type ExternalOrganization = {
  id: number;
  name: string;
  team_id: number;
  people: ExternalPerson[];
};

export type ExternalOrganizationLink = {
  id: number;
  grant_opportunity_id: number;
  external_organization_id: number;
  name: string;
};

export async function listExternalOrganizations(municipalityId: number): Promise<ExternalOrganization[]> {
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/external_organizations`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error("Failed to load external organizations");
  }
  return response.json();
}

export async function createExternalOrganization(
  municipalityId: number,
  name: string,
): Promise<ExternalOrganization> {
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/external_organizations`, {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders(),
    body: JSON.stringify({ external_organization: { name } }),
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Failed to create external organization"));
  }
  return response.json();
}

export async function addExternalPerson(
  municipalityId: number,
  organizationId: number,
  person: { email: string; name?: string },
): Promise<ExternalPerson> {
  const response = await fetch(
    `/api/v1/municipalities/${municipalityId}/external_organizations/${organizationId}/people`,
    {
      method: "POST",
      credentials: "include",
      headers: jsonHeaders(),
      body: JSON.stringify({ external_person: person }),
    },
  );
  if (!response.ok) {
    // A team member's email is refused here; surface that reason.
    throw new Error(await errorMessage(response, "Failed to add person"));
  }
  return response.json();
}

export async function queueInviteRequest(
  municipalityId: number,
  organizationId: number,
  externalPersonId: number,
): Promise<void> {
  const response = await fetch(
    `/api/v1/municipalities/${municipalityId}/external_organizations/${organizationId}/invite_requests`,
    {
      method: "POST",
      credentials: "include",
      headers: jsonHeaders(),
      body: JSON.stringify({ invite_request: { external_person_id: externalPersonId } }),
    },
  );
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Failed to queue invite"));
  }
}

export async function listExternalOrganizationLinks(
  municipalityId: number,
  opportunityId: number,
): Promise<ExternalOrganizationLink[]> {
  const response = await fetch(linksUrl(municipalityId, opportunityId), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error("Failed to load linked organizations");
  }
  return response.json();
}

export async function linkExternalOrganization(
  municipalityId: number,
  opportunityId: number,
  organizationId: number,
): Promise<ExternalOrganizationLink> {
  const response = await fetch(linksUrl(municipalityId, opportunityId), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders(),
    body: JSON.stringify({ external_organization_link: { external_organization_id: organizationId } }),
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Failed to link organization"));
  }
  return response.json();
}

export async function unlinkExternalOrganization(
  municipalityId: number,
  opportunityId: number,
  linkId: number,
): Promise<void> {
  const response = await fetch(`${linksUrl(municipalityId, opportunityId)}/${linkId}`, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Failed to unlink organization"));
  }
}

function linksUrl(municipalityId: number, opportunityId: number): string {
  return `/api/v1/municipalities/${municipalityId}/grant_opportunities/${opportunityId}/external_organization_links`;
}

function jsonHeaders(): Record<string, string> {
  return { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() };
}

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

async function errorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    const errors = body?.errors;
    if (Array.isArray(errors) && errors.length > 0) return errors.join(", ");
    // 404/403 responses use a singular error key.
    if (typeof body?.error === "string" && body.error.length > 0) return body.error;
  } catch {
    // Non-JSON body; fall through to the generic message.
  }
  return fallback;
}
